import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { procurementApi } from 'api/procurement';

import { TemperatureConflictDialog } from './TemperatureConflictDialog';
import { WeekYearUpdateDialog } from './WeekYearUpdateDialog';
import { PurchaseOrderCreateItemsCard } from './create/PurchaseOrderCreateItemsCard';
import { PurchaseOrderCreatePageHeader } from './create/PurchaseOrderCreatePageHeader';
import { PurchaseOrderCreatePrimaryCard } from './create/PurchaseOrderCreatePrimaryCard';
import { usePurchaseOrderQuery, usePurchaseOrdersV2Search } from './queries/PurchaseOrderQueries';
import * as purchaseOrderCreateUtils from './utils/purchaseOrderCreateAndEditUtils';
import { findWeekAndYearForScheduledDeliveryDate } from './utils/purchaseOrderDateUtils';

export function CreatePurchaseOrder() {
    const location = useLocation();
    const history = useHistory();

    // Maintain filters from the purchase orders table for back navigation (same pattern as entity view)
    const purchaseOrdersFiltersFromState =
        typeof location.state?.purchaseOrdersFilters === 'string' && location.state.purchaseOrdersFilters.length > 0
            ? location.state.purchaseOrdersFilters
            : '';
    const purchaseOrdersFilters = useMemo(() => {
        if (purchaseOrdersFiltersFromState) return purchaseOrdersFiltersFromState;

        const rawSearch =
            (typeof location?.search === 'string' && location.search ? location.search : '') ||
            (typeof window !== 'undefined' && window?.location?.search ? window.location.search : '') ||
            '';
        if (!rawSearch) return '';

        const params = new URLSearchParams(rawSearch);
        params.delete('duplicateFrom');
        const next = params.toString();
        return next ? `?${next}` : '';
    }, [location?.search, purchaseOrdersFiltersFromState]);
    const backHref = purchaseOrdersFilters
        ? { pathname: '/procurement/viewPurchaseOrder-v2', search: purchaseOrdersFilters }
        : '/procurement/viewPurchaseOrder-v2';

    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const duplicateFromId = searchParams.get('duplicateFrom');

    const { data: sourcePOSearch, isLoading: isLoadingSourcePO } = usePurchaseOrdersV2Search(
        { poNumber: duplicateFromId },
        { enabled: Boolean(duplicateFromId) },
    );
    const sourcePO = sourcePOSearch?.rows?.[0] ?? null;
    const { data: sourcePODetail, isLoading: isLoadingSourcePODetail } = usePurchaseOrderQuery({
        id: duplicateFromId,
        enabled: Boolean(duplicateFromId),
    });
    const isDuplicateLoading = Boolean(duplicateFromId) && (isLoadingSourcePO || isLoadingSourcePODetail);

    const [draft, setDraft] = useState(purchaseOrderCreateUtils.DEFAULT_DRAFT);
    const [items, setItems] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
    const [itemEditsVersion, setItemEditsVersion] = useState(0);
    const itemEditStateRef = useRef({ inputs: {}, selects: {}, multi: {} });
    const [removedItemIds, setRemovedItemIds] = useState(new Set());
    const [scheduledDeliveryDateInput, setScheduledDeliveryDateInput] = useState(
        purchaseOrderCreateUtils.DEFAULT_DRAFT.scheduledDeliveryDate ?? '',
    );
    const lastDraftScheduledDateRef = useRef(purchaseOrderCreateUtils.DEFAULT_DRAFT.scheduledDeliveryDate ?? '');
    const [tempConflictDialogOpen, setTempConflictDialogOpen] = useState(false);
    const [tempConflictStorageZone, setTempConflictStorageZone] = useState('');
    const [weekYearDialogState, setWeekYearDialogState] = useState({
        open: false,
        nextDate: '',
        nextWeek: '',
        nextYear: '',
        prevDate: '',
        prevTime: '',
    });
    const pageTitle = useMemo(() => 'New Purchase Order', []);

    const hasInitializedDuplicateRef = useRef(false);

    useEffect(() => {
        if (!duplicateFromId) return;
        if (isLoadingSourcePO || isLoadingSourcePODetail) return;
        if (!sourcePO && !sourcePODetail) return;
        if (hasInitializedDuplicateRef.current) return;

        const initialDraft = purchaseOrderCreateUtils.buildDuplicatePrimaryDraft(sourcePO);
        setDraft(initialDraft);

        const initialItems = purchaseOrderCreateUtils.buildDuplicateItems(sourcePODetail);
        setItems(initialItems);
        hasInitializedDuplicateRef.current = true;
    }, [duplicateFromId, sourcePO, sourcePODetail, isLoadingSourcePO, isLoadingSourcePODetail]);

    useEffect(() => {
        const nextDraftDate = draft?.scheduledDeliveryDate ?? '';
        if (scheduledDeliveryDateInput === lastDraftScheduledDateRef.current) {
            setScheduledDeliveryDateInput(nextDraftDate);
        }
        lastDraftScheduledDateRef.current = nextDraftDate;
    }, [draft?.scheduledDeliveryDate, scheduledDeliveryDateInput]);

    const validation = useMemo(
        () =>
            purchaseOrderCreateUtils.validatePurchaseOrderCreate({
                draft,
                itemEditStateRef,
                items,
                removedItemIds,
            }),
        [draft, items, itemEditStateRef, removedItemIds, itemEditsVersion],
    );

    const handleItemEditsChange = useCallback(() => {
        setItemEditsVersion((prev) => prev + 1);
    }, []);

    const handleTempConflictDialogClose = useCallback(() => {
        setTempConflictDialogOpen(false);
        setTempConflictStorageZone('');
    }, []);

    const handleItemAdded = useCallback(
        ({ itemMaster }) => {
            const tempRange = purchaseOrderCreateUtils.getTempRangeForStorageZone(itemMaster?.storageTempZone);
            if (!tempRange) return;

            const draftMin = draft?.minTempReq ?? '';
            const draftMax = draft?.maxTempReq ?? '';
            const hasDraftRange = String(draftMin) !== '' && String(draftMax) !== '';
            if (!hasDraftRange && (Array.isArray(items) ? items.length : 0) === 0) {
                setDraft((prev) => ({
                    ...prev,
                    minTempReq: String(tempRange.min),
                    maxTempReq: String(tempRange.max),
                }));
                return;
            }

            if (!hasDraftRange) return;

            const normalizedMin = String(draftMin);
            const normalizedMax = String(draftMax);
            if (normalizedMin !== String(tempRange.min) || normalizedMax !== String(tempRange.max)) {
                setTempConflictStorageZone(String(itemMaster?.storageTempZone ?? ''));
                setTempConflictDialogOpen(true);
            }
        },
        [draft?.maxTempReq, draft?.minTempReq, items],
    );

    const applyScheduledDeliveryDate = useCallback((nextDate) => {
        setDraft((prev) => ({
            ...prev,
            scheduledDeliveryDate: nextDate,
            // When a date is selected and time is empty, default to midnight.
            scheduledTime: nextDate ? prev?.scheduledTime || '00:00' : '',
        }));
    }, []);

    const getCurrentItemWeekYear = useCallback(
        (row) => {
            const rowId = String(row?.id ?? '');
            const weekInput = purchaseOrderCreateUtils.getItemEditInputValue(itemEditStateRef, rowId, 'week');
            const yearInput = purchaseOrderCreateUtils.getItemEditInputValue(itemEditStateRef, rowId, 'year');
            const weekValue = weekInput === undefined ? row?.week : weekInput;
            const yearValue = yearInput === undefined ? row?.year : yearInput;
            return {
                week: weekValue === null || weekValue === undefined || weekValue === '' ? '' : String(weekValue),
                year: yearValue === null || yearValue === undefined || yearValue === '' ? '' : String(yearValue),
            };
        },
        [itemEditStateRef],
    );

    const hasWeekYearMismatch = useCallback(
        (nextWeekYear) => {
            if (!nextWeekYear) return false;
            const nextWeek = nextWeekYear?.week == null ? '' : String(nextWeekYear.week);
            const nextYear = nextWeekYear?.year == null ? '' : String(nextWeekYear.year);
            const removed = removedItemIds instanceof Set ? removedItemIds : new Set();
            return (Array.isArray(items) ? items : []).some((row) => {
                if (!row || removed.has(String(row?.id))) return false;
                const current = getCurrentItemWeekYear(row);
                return current.week !== nextWeek || current.year !== nextYear;
            });
        },
        [getCurrentItemWeekYear, items, removedItemIds],
    );

    const handleScheduledDeliveryDateChange = useCallback(
        (nextDate) => {
            const nextWeekYear = nextDate ? findWeekAndYearForScheduledDeliveryDate(nextDate) : null;
            const shouldPrompt =
                nextWeekYear && Array.isArray(items) && items.length > 0 && hasWeekYearMismatch(nextWeekYear);
            if (!shouldPrompt) {
                applyScheduledDeliveryDate(nextDate);
                return false;
            }

            const nextWeek = nextWeekYear?.week == null ? '' : String(nextWeekYear.week);
            const nextYear = nextWeekYear?.year == null ? '' : String(nextWeekYear.year);
            setWeekYearDialogState({
                open: true,
                nextDate,
                nextWeek,
                nextYear,
                prevDate: draft?.scheduledDeliveryDate ?? '',
                prevTime: draft?.scheduledTime ?? '',
            });
            applyScheduledDeliveryDate(nextDate);
            return true;
        },
        [applyScheduledDeliveryDate, draft?.scheduledDeliveryDate, draft?.scheduledTime, hasWeekYearMismatch, items],
    );

    const handleWeekYearDialogCancel = useCallback(() => {
        const { prevDate, prevTime } = weekYearDialogState;
        setWeekYearDialogState((prev) => ({ ...prev, open: false }));
        setDraft((prev) => ({
            ...prev,
            scheduledDeliveryDate: prevDate,
            scheduledTime: prevTime ?? '',
        }));
    }, [weekYearDialogState]);

    const handleWeekYearDialogKeep = useCallback(() => {
        setWeekYearDialogState((prev) => ({ ...prev, open: false }));
    }, []);

    const handleWeekYearDialogUpdate = useCallback(() => {
        const { nextWeek, nextYear } = weekYearDialogState;
        const removed = removedItemIds instanceof Set ? removedItemIds : new Set();
        const currentItems = Array.isArray(items) ? items : [];

        const inputs = itemEditStateRef?.current?.inputs ?? {};
        currentItems.forEach((row) => {
            if (!row || removed.has(String(row?.id))) return;
            const rowId = String(row.id);
            if (!inputs[rowId]) inputs[rowId] = {};
            inputs[rowId].week = nextWeek;
            inputs[rowId].year = nextYear;
        });
        if (itemEditStateRef?.current) {
            itemEditStateRef.current.inputs = inputs;
        }

        setItems((prev) =>
            (Array.isArray(prev) ? prev : []).map((row) => {
                if (!row || removed.has(String(row?.id))) return row;
                return {
                    ...row,
                    week: nextWeek,
                    year: nextYear,
                };
            }),
        );

        if (typeof handleItemEditsChange === 'function') {
            handleItemEditsChange();
        }
        setWeekYearDialogState((prev) => ({ ...prev, open: false }));
    }, [
        handleItemEditsChange,
        itemEditStateRef,
        items,
        removedItemIds,
        weekYearDialogState.nextWeek,
        weekYearDialogState.nextYear,
    ]);

    const handleSave = useCallback(async () => {
        if (isSubmitting) return;

        const normalizedInputDate = String(scheduledDeliveryDateInput ?? '');
        const currentDraftDate = String(draft?.scheduledDeliveryDate ?? '');
        if (normalizedInputDate !== currentDraftDate) {
            const didPrompt = handleScheduledDeliveryDateChange(normalizedInputDate);
            if (didPrompt) return;
        }

        const draftForSubmit =
            normalizedInputDate === currentDraftDate
                ? draft
                : {
                      ...draft,
                      scheduledDeliveryDate: normalizedInputDate,
                      scheduledTime: normalizedInputDate ? draft?.scheduledTime || '00:00' : '',
                  };
        const saveValidation = purchaseOrderCreateUtils.validatePurchaseOrderCreate({
            draft: draftForSubmit,
            itemEditStateRef,
            items,
            removedItemIds,
        });

        setHasAttemptedSubmit(true);
        if (!saveValidation.isValid) {
            if (normalizedInputDate !== currentDraftDate) {
                setDraft(draftForSubmit);
            }
            return;
        }

        setIsSubmitting(true);
        try {
            const facilityIDs = draftForSubmit.facilities.map((f) => f?.id).filter((id) => id != null);
            const sendParams = {
                vendorID: draftForSubmit.vendorId,
                deliveryType: draftForSubmit.deliveryType,
                pickUpDate: draftForSubmit.pickUpDate,
                originalRequestedDeliveryDate: draftForSubmit.originalRequestedDeliveryDate,
                deliverDate: draftForSubmit.scheduledDeliveryDate,
                deliverTime: draftForSubmit.scheduledTime,
                crossDockID: draftForSubmit.crossDockId || '',
                crossDockDeliveryDate: draftForSubmit.crossDockDeliveryDate,
                specialInstructions: draftForSubmit.specialInstructions,
                inboundShipperID: draftForSubmit.shipperId ?? '',
                facilityIDs,
                items: saveValidation.submitItems,
                buyers: draftForSubmit.buyers,
                minTempReq: draftForSubmit.minTempReq,
                maxTempReq: draftForSubmit.maxTempReq,
                priority: draftForSubmit.priority === true,
                estimatedFreightCost:
                    draftForSubmit.deliveryType === 'DELIVERED' ? draftForSubmit.estimatedFreightCost : null,
                isReceivedAtCrossDock: draftForSubmit.isReceivedAtCrossDock === true,
            };

            const response = await procurementApi.createPurchaseOrder(sendParams);
            if (response?.status === false) {
                const message = response?.msg
                    ? `Error creating purchase order: ${response.msg}`
                    : 'Error creating purchase order';
                toast.error(message);
                return;
            }

            toast.success('Purchase Order created!');
            history.push(backHref);
        } catch (err) {
            toast.error(err?.message ?? 'Error creating purchase order');
        } finally {
            setIsSubmitting(false);
        }
    }, [
        backHref,
        draft,
        handleScheduledDeliveryDateChange,
        history,
        isSubmitting,
        itemEditStateRef,
        items,
        removedItemIds,
        scheduledDeliveryDateInput,
    ]);

    return (
        <div className="flex min-h-screen flex-col gap-6 px-4 py-4">
            <PurchaseOrderCreatePageHeader
                title={pageTitle}
                statusLabel="Creating"
                onBack={() => history.push(backHref)}
                onCancel={() => history.push(backHref)}
                onSave={handleSave}
                isSaveDisabled={isSubmitting}
                isLoadingDuplicate={isDuplicateLoading}
            />

            <PurchaseOrderCreatePrimaryCard
                draft={draft}
                onDraftChange={setDraft}
                scheduledDeliveryDateInput={scheduledDeliveryDateInput}
                onScheduledDeliveryDateInputChange={setScheduledDeliveryDateInput}
                onScheduledDeliveryDateChange={handleScheduledDeliveryDateChange}
                showValidation={hasAttemptedSubmit}
                primaryErrors={validation.primaryErrors}
                primaryFieldErrors={validation.primaryFieldErrors}
            />

            <PurchaseOrderCreateItemsCard
                items={items}
                onItemsChange={setItems}
                itemEditStateRef={itemEditStateRef}
                removedItemIds={removedItemIds}
                setRemovedItemIds={setRemovedItemIds}
                vendorId={draft.vendorId}
                scheduledDeliveryDate={draft.scheduledDeliveryDate}
                showValidation={hasAttemptedSubmit}
                itemErrors={validation.itemErrors}
                itemSummaryErrors={validation.itemSummaryErrors}
                onItemEditsChange={handleItemEditsChange}
                onItemAdded={handleItemAdded}
            />

            <WeekYearUpdateDialog
                open={weekYearDialogState.open}
                title="Update item week/year?"
                description={`This scheduled delivery date calculates to week ${weekYearDialogState.nextWeek}/${weekYearDialogState.nextYear}, which differs from the current item values. Update all items to match?`}
                onCancel={handleWeekYearDialogCancel}
                onKeep={handleWeekYearDialogKeep}
                onUpdate={handleWeekYearDialogUpdate}
                updateLabel="Update week/year"
                keepLabel="Keep current"
                cancelLabel="Cancel"
            />

            <TemperatureConflictDialog
                isOpen={tempConflictDialogOpen}
                onClose={handleTempConflictDialogClose}
                storageTempZone={tempConflictStorageZone}
            />
        </div>
    );
}
